import React, { useState, useEffect, useRef } from "react";
import { Modal, Button, Form, Alert } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { runbookActions } from "../../actions/runbook.actions";
import { runbookServerActions } from "../../actions/runbookServer.actions";
import { runbookTagService } from "../../services/runbookTag.service";

function EditRunbookModal({ show, onHide, runbook, user, onSuccess }) {
  const dispatch = useDispatch();
  const runbookReducer = useSelector((state) => state.runbook);
  const runbookServerReducer = useSelector((state) => state.runbookServer);
  const previousLoadingRef = useRef(false);

  const [formData, setFormData] = useState({
    runbookName: "",
    description: "",
    isTemplate: false,
    debugMode: false,
    runbookServer: "",
    netChangeField: "",
    netChangeResult: "",
    tagId: "",
  });

  const [errors, setErrors] = useState({});
  const [tags, setTags] = useState([]);

  // #2647: tags must be loaded whenever this modal opens, not just once on mount -- it stays
  // mounted inside RunbookGrid for the page lifetime.
  useEffect(() => {
    if (show && user.clientId) {
      runbookTagService.get({ clientId: user.clientId }).then((response) => {
        setTags(response.data || []);
      });
    }
  }, [show, user.clientId]);

  // Fetch runbook servers when the modal opens (only for super users/product team — matches
  // the backend's /runbookserver/getall authorization, which does not accept Admin alone).
  // This component is always mounted inside RunbookGrid (visibility is just the `show` prop),
  // so gating on `show` also avoids firing this on every Runbooks page load for every user.
  useEffect(() => {
    if (show && !runbookServerReducer.response && (user.isSuperUser || user.isProductTeam)) {
      dispatch(runbookServerActions.getAll());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  // Initialize form with runbook data when modal opens
  useEffect(() => {
    if (runbook && show) {
      setFormData({
        runbookName: runbook.runbookName || "",
        description: runbook.description || "",
        isTemplate: runbook.isTemplate || false,
        debugMode: runbook.debugMode || false,
        runbookServer: runbook.runbookServer || "",
        netChangeField: runbook.netChangeField || "",
        // Take first 23 chars (YYYY-MM-DDTHH:mm:ss.sss) to preserve milliseconds
        netChangeResult: runbook.netChangeResult ? runbook.netChangeResult.slice(0, 23) : "",
        tagId: runbook.tagId || "",
      });
      setErrors({});
    }
  }, [runbook, show]);

  const handleChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    // Clear error for this field when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({ ...prev, [field]: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    if (!formData.runbookName || formData.runbookName.trim() === "") {
      newErrors.runbookName = "Runbook name is required";
    }

    // Only validate runbook server for users who can change it
    if ((user.isSuperUser || user.isAdmin || user.isProductTeam) &&
        (!formData.runbookServer || formData.runbookServer.trim() === "")) {
      newErrors.runbookServer = "Runbook server is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    const requestBody = {
      runbookId: runbook.runbookId,
      clientId: runbook.clientId,
      clientGuid: runbook.clientGuid,
      runbookName: formData.runbookName,
      description: formData.description,
      isTemplate: formData.isTemplate,
      debugMode: formData.debugMode,
      runbookServer: formData.runbookServer,
      netChangeField: formData.netChangeField || null,
      // Send as-is, no timezone conversion
      netChangeResult: formData.netChangeResult || null,
      tagId: formData.tagId || null,
    };

    dispatch(runbookActions.update(requestBody));
  };

  // Monitor update success and close modal
  useEffect(() => {
    // Only close modal when we transition from loading to success
    if (
      show &&
      previousLoadingRef.current === true &&
      runbookReducer.loadingUpdate === false &&
      runbookReducer.updateSuccess === true
    ) {
      onHide();
      if (onSuccess) {
        onSuccess();
      }
    }
    previousLoadingRef.current = runbookReducer.loadingUpdate;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show, runbookReducer.loadingUpdate, runbookReducer.updateSuccess]);

  if (!runbook) {
    return null;
  }

  return (
    <Modal show={show} onHide={onHide} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit Runbook - {runbook.runbookId}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form>
          <Form.Group className="mb-3">
            <Form.Label>
              Runbook Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              value={formData.runbookName}
              onChange={(e) => handleChange("runbookName", e.target.value)}
              isInvalid={!!errors.runbookName}
              placeholder="Enter runbook name"
            />
            <Form.Control.Feedback type="invalid">
              {errors.runbookName}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Tag</Form.Label>
            <Form.Select
              value={formData.tagId}
              onChange={(e) => handleChange("tagId", e.target.value)}
            >
              <option value="">Ungrouped</option>
              {tags.map((t) => (
                <option key={t.tagId} value={t.tagId}>
                  {t.name}
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Optional -- groups this runbook with others under the same tag. Groups themselves
              are managed under Administration &gt; Runbook Groups.
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Description</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              value={formData.description}
              onChange={(e) => handleChange("description", e.target.value)}
              placeholder="Enter runbook description"
            />
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>
              Runbook Server {(user.isSuperUser || user.isAdmin || user.isProductTeam) && <span className="text-danger">*</span>}
            </Form.Label>
            {(user.isSuperUser || user.isAdmin || user.isProductTeam) ? (
              <Form.Select
                value={formData.runbookServer}
                onChange={(e) => handleChange("runbookServer", e.target.value)}
                isInvalid={!!errors.runbookServer}
              >
                <option value="">Select a server</option>
                {runbookServerReducer.response?.map((server) => (
                  <option key={server.serverName} value={server.serverName}>
                    {server.serverName}
                  </option>
                ))}
              </Form.Select>
            ) : (
              <Form.Control
                type="text"
                value={formData.runbookServer}
                disabled
                readOnly
              />
            )}
            <Form.Control.Feedback type="invalid">
              {errors.runbookServer}
            </Form.Control.Feedback>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Is Template"
              checked={formData.isTemplate}
              onChange={(e) => handleChange("isTemplate", e.target.checked)}
            />
            <Form.Text className="text-muted">
              Mark this runbook as a template for creating new runbooks
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Check
              type="checkbox"
              label="Debug Mode"
              checked={formData.debugMode}
              onChange={(e) => handleChange("debugMode", e.target.checked)}
            />
            <Form.Text className="text-muted">
              Enable debug mode for detailed execution logging
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Net Change Field</Form.Label>
            <Form.Control
              type="text"
              value={formData.netChangeField}
              onChange={(e) => handleChange("netChangeField", e.target.value)}
              placeholder="Enter field name for net change tracking"
              maxLength={100}
            />
            <Form.Text className="text-muted">
              The field name used to track changes (max 100 characters)
            </Form.Text>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label>Net Change Result</Form.Label>
            <Form.Control
              type="datetime-local"
              step="0.001"
              value={formData.netChangeResult}
              onChange={(e) => handleChange("netChangeResult", e.target.value)}
            />
            <Form.Text className="text-muted">
              The last datetime value used for net change comparison
            </Form.Text>
          </Form.Group>

          {runbookReducer.updateError && (
            <Alert variant="danger" className="mt-3">
              Failed to update runbook. Please try again.
            </Alert>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={onHide}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={handleSubmit}
          disabled={runbookReducer.loadingUpdate}
        >
          {runbookReducer.loadingUpdate ? "Saving..." : "Save Changes"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default EditRunbookModal;
